import React, { useState, useMemo, useRef } from 'react';
import { isMobile } from "react-device-detect";
import { usePallet } from "../../../contexts/PalletContext";
import { IParlamentar } from "../../../models/Parlamentar.Model";
import html2canvas from 'html2canvas';
import CloseIcon from '@mui/icons-material/Close';
import {
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Tooltip,
    Box,
    Divider,
    Link,
    DialogActions,
    Button,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import jsPDF from 'jspdf';

interface Props {
    parlamentar?: {
        perfil?: boolean;
        nome_civil?: string;
        nome?: string;
    };
}


interface Perfil {
    foto: string;
    nome_completo: string;
    apelido: string;
    partido: { sigla: string; motivo: string };
    formacao_profissional: {
        profissao: string;
        formacoes: { curso: string; instituicao: string; motivo: string }[];
    };
    contatos: {
        diretos: { tipo: string; detalhe: string }[];
        indiretos: { tipo: string; detalhe: string }[];
    };
    financiadores: { nome: string; quantia: string; origem: string }[];
    ocorrencias_juridicas: { tipo: string; detalhe: string }[];
    temas_de_atuacao: { defende: string[]; ataca: string[] };
    historico_politico: { ano: number; cargo: string; votos: number; eleitorado: string }[];
    projetos: {
        propostos: { nome: string; descricao: string }[];
        participacoes: { nome: string; descricao: string }[];
    };
    fontes: string[];
}

const ParlamentarConsultaSpanCustom = (props) => {
    const { pallet } = usePallet()
    return (
        <span style={{
            fontFamily: `${pallet.fontFamily.general}`,
            fontWeight: pallet.fontWeight.subtitle,
            fontSize: `${pallet.fontSize.smallButton}`,
            ...props?.style
        }}>
            {props?.children}
        </span>
    )
}

const ParlamentarConsultaTypography = (props) => {
    const { pallet } = usePallet()
    return (
        <Typography align="justify" style={{
            fontFamily: `${pallet.fontFamily.general}`,
            fontWeight: pallet.fontWeight.subtitle,
            fontSize: `${pallet.fontSize.titleCardParlamentar}`,
            color: `${pallet.color.colorBase.black}`,
            ...props?.style
        }}>
            {props?.children}
        </Typography>
    )
}

const ParlamentarApelidoConsultaTypography = (props) => {
    const { pallet } = usePallet()
    return (
        <Typography align="justify" style={{
            fontFamily: `${pallet.fontFamily.general}`,
            fontWeight: 500,
            fontSize: '15px',
            paddingTop: '2px',
            color: `${pallet.color.colorBase.black}`,
            ...props?.style
        }}>
            {props?.children}
        </Typography>
    )
}

const ParlamentarConsultaSituacaoTypography = (props: { children: any, style?: any }) => {
    const { pallet } = usePallet()
    return (
        <Typography align="justify" style={{
            padding: "3px 9px",
            borderRadius: "13px",
            backgroundColor: `${pallet.color.colorBase.greyBackground}`,
            fontFamily: `${pallet.fontFamily.general}`,
            fontWeight: pallet.fontWeight.subtitle,
            fontSize: `${pallet.fontSize.text_10}`,
            color: `${pallet.color.colorBase.greyText}`,
            marginBottom: "15px",
            textTransform: "uppercase",
            ...props?.style
        }}>
            {props.children}
        </Typography>
    )
}

export const ParlamentarConsultaInicio = (props: {
    parlamentar: IParlamentar, idPermissao?: number, statusProcessamentoIa?: boolean;
}) => {

    const { pallet } = usePallet()
    const [openPerfil, setOpenPerfil] = useState(false);
    const pdfRef = useRef<HTMLDivElement>(null);

    const abrirEmPopupLimpo = () => {
        const url = props.parlamentar?.divulgacandcontas_url;

        if (!url) return;

        const largura = Math.floor(window.screen.availWidth * 0.8);
        const altura = Math.floor(window.screen.availHeight * 0.8);
        const left = Math.floor((window.screen.availWidth - largura) / 2);
        const top = Math.floor((window.screen.availHeight - altura) / 2);

        const novaJanela = window.open(
            url,
            '_blank',
            `width=${largura},height=${altura},left=${left},top=${top},` +
            `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        if (novaJanela) {
            novaJanela.focus();
        } else {
            alert('Popup bloqueado. Habilite pop-ups para este site.');
        }
    };

    const perfilData = useMemo<Perfil | null>(() => {
        const raw = props.parlamentar?.perfil;
        if (!raw) return null;

        // Se já for objeto, devolve direto
        if (typeof raw !== 'string') {
            return raw as Perfil;
        }

        // ---- a partir daqui, raw é string ----

        // 1) Remove pontos de milhar
        let cleaned = raw.replace(/(\d)\.(?=\d{3}(\D|$))/g, '$1');

        // 2) Remove marcadores de Markdown (se ainda existir)
        cleaned = cleaned
            .replace(/^```(?:json)?\s*/, '')
            .replace(/```$/, '')
            .trim();

        // 3) Tenta parsear
        try {
            return JSON.parse(cleaned) as Perfil;
        } catch (err) {
            console.error('Falha ao JSON.parse do perfil:', err);
            return null;
        }
    }, [props.parlamentar?.perfil]);

    const handleDownloadPdf = async () => {
        if (!pdfRef.current) return;
        const canvas = await html2canvas(pdfRef.current, { scale: 2 });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgProps = pdf.getImageProperties(imgData);
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`perfil-parlamentar-${props.parlamentar?.nome}.pdf`);
    };

    return (
        <Grid item>
            <div style={{ paddingLeft: `${isMobile ? "20px" : "50px"}`, marginTop: "35px" }}>
                <Grid >
                    <Grid item >
                        <Grid container>
                            {props.parlamentar?.situacao && props.parlamentar?.situacao !== '' ?
                                <ParlamentarConsultaSituacaoTypography>
                                    {props.parlamentar?.situacao}
                                </ParlamentarConsultaSituacaoTypography>
                                :
                                null
                            }
                            {props.idPermissao !== 4 ?
                                <ParlamentarConsultaSituacaoTypography style={{ marginLeft: "10px" }}>
                                    {props.parlamentar?.cliente?.qualificacao || 'Não Classificado'}
                                </ParlamentarConsultaSituacaoTypography>
                                :
                                null}
                        </Grid>
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                                <ParlamentarConsultaTypography sx={{ textTransform: 'uppercase' }}>
                                    {props.parlamentar?.nome_civil}
                                </ParlamentarConsultaTypography>
                            </Grid>
                            <Grid item>
                                <ParlamentarApelidoConsultaTypography sx={{ textTransform: 'uppercase' }}>
                                    &nbsp;({props.parlamentar?.nome})
                                </ParlamentarApelidoConsultaTypography>
                            </Grid>
                        </Grid>


                        <Grid container>
                            <Typography style={{
                                color: `${pallet.color.colorBase.black}`,
                            }}>
                                <ParlamentarConsultaSpanCustom >
                                    {props.parlamentar?.tratamento}
                                </ParlamentarConsultaSpanCustom>
                                <span>
                                    {props.parlamentar?.sigla_partido ? " • " : ""}
                                </span>
                                <ParlamentarConsultaSpanCustom style={{ textTransform: "uppercase" }}>
                                    {props.parlamentar?.sigla_partido}
                                </ParlamentarConsultaSpanCustom>
                                <span>
                                    {props.parlamentar?.uf_exercicio ? " • " : ""}
                                </span>
                                <ParlamentarConsultaSpanCustom style={{ textTransform: "uppercase", }}>
                                    {props.parlamentar?.uf_exercicio}
                                </ParlamentarConsultaSpanCustom>
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </div >
        </Grid >
    )
}